import { BRAND_PERSON_NAME, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
/**
 * Checkout AR Channel Invite - MercadoPago Bricks
 * 
 * Página de checkout exclusiva para Argentina con experiencia embebida de MercadoPago Bricks.
 * No redirige a MercadoPago, todo el flujo ocurre en la misma página.
 * 
 * Flujo:
 * 1. Crea preference via /api/mercadopago/create-preference
 * 2. Muestra MercadoPago Payment Brick (formulario embebido)
 * 3. Usuario completa el pago → onSuccess → redirect a success page
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MercadoPagoCheckoutForm } from "@/components/MercadoPagoCheckoutForm";
import { AR_PLANS } from "@/config/arInvitacionPlans";
import { Loader2, AlertCircle } from "lucide-react";

const PRODUCT_NAME = `Canal Exclusivo ${BRAND_PERSON_NAME}`;
const PRODUCT_DESCRIPTION = "Acceso mensual al canal exclusivo de Telegram";
const PRICE_ARS = AR_PLANS.standard.price; // 29.999 ARS

export default function CheckoutArChannelInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get optional parameters from URL
  const invitationCode = searchParams.get("ref") || searchParams.get("code") || undefined;
  const language = (searchParams.get("lang") || "es") as "en" | "es";

  // Create MercadoPago preference on mount
  useEffect(() => {
    const createPreference = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Meta attribution: leemos utm_* + fbclid del URL + cookies _fbp/_fbc
        // y los mandamos al backend para que queden en preference.metadata.
        // Sin esto, el webhook MP no puede mandar CAPI Purchase con la fuente
        // real del ad → Meta lo atribuye a "direct/organic" y rompe la
        // optimización. Audit 2026-06-14.
        const urlParams =
          typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
        const cookies =
          typeof document !== "undefined" ? document.cookie : "";
        const cookieFbp = cookies.match(/(?:^|; )_fbp=([^;]+)/)?.[1];
        const cookieFbc = cookies.match(/(?:^|; )_fbc=([^;]+)/)?.[1];

        const response = await fetch("/api/mercadopago/create-preference", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productId: `channel_invite_ar${invitationCode ? `_${invitationCode}` : ""}`,
            productName: PRODUCT_NAME,
            price: PRICE_ARS,
            currency: "ARS",
            country: "AR",
            successPath: "/ar/invitacion/success",
            failurePath: "/checkout-ar-channel-invite?error=payment_failed",
            pendingPath: "/ar/invitacion/pending",
            metadata: {
              type: "membresia",
              source: BRAND_SOURCE_KEY,
              flow: "channel_invite_ar",
              product_type: "channel_invite",
              invitation_code: invitationCode || undefined,
              price_ars: PRICE_ARS,
              utm_source: urlParams.get("utm_source") || undefined,
              utm_medium: urlParams.get("utm_medium") || undefined,
              utm_campaign: urlParams.get("utm_campaign") || undefined,
              utm_content: urlParams.get("utm_content") || undefined,
              utm_term: urlParams.get("utm_term") || undefined,
              fbclid: urlParams.get("fbclid") || undefined,
              fbp: cookieFbp || undefined,
              fbc: cookieFbc || undefined,
              page_url:
                typeof window !== "undefined" ? window.location.href : undefined,
              referrer:
                typeof document !== "undefined" ? document.referrer || undefined : undefined,
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({})) as { error?: string };
          throw new Error(errorData.error || "Error al crear el checkout");
        }

        const data = await response.json() as { preferenceId?: string; id?: string };
        const prefId = data.preferenceId || data.id;

        if (!prefId) {
          throw new Error("No se recibió el ID de preferencia");
        }

        setPreferenceId(prefId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error de conexión. Intenta de nuevo.";
        setError(msg);
        console.error("[CheckoutArChannelInvite] Error creating preference:", err);
      } finally {
        setIsLoading(false);
      }
    };

    createPreference();
  }, [invitationCode]);

  // Handle successful payment
  const handleSuccess = (paymentId: number, status: string) => {
    console.log("[CheckoutArChannelInvite] Payment successful:", { paymentId, status });
    
    // Redirect to success page with payment info
    const successUrl = status === "pending" 
      ? `/ar/invitacion/pending?payment_id=${paymentId}`
      : `/ar/invitacion/success?payment_id=${paymentId}`;
    
    window.location.href = successUrl;
  };

  // Handle back button
  const handleBack = () => {
    // Go back to the channel invite page
    navigate("/canal");
  };

  // Handle error
  const handleError = (err: string) => {
    setError(err);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="w-full h-dvh flex flex-col items-center justify-center bg-[#0A0E27] text-white font-body">
        <Loader2 className="w-12 h-12 animate-spin text-[#009EE3] mb-4" />
        <p className="text-white/70 text-sm font-medium">Preparando checkout...</p>
      </div>
    );
  }

  // Error state
  if (error || !preferenceId) {
    return (
      <div className="w-full h-dvh flex flex-col items-center justify-center bg-[#0A0E27] text-white p-6 font-body">
        <div className="max-w-md w-full space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2 font-display">Error al cargar el checkout</h2>
            <p className="text-red-200 text-sm mb-4 font-medium">{error || "No se pudo inicializar el pago"}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#009EE3] to-[#0077B5] rounded-xl font-bold text-white hover:opacity-90 transition-opacity"
            >
              Reintentar
            </button>
          </div>
          <button
            onClick={handleBack}
            className="w-full py-3 text-white/60 hover:text-white text-sm font-semibold transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // Render MercadoPago checkout form
  return (
    <div className="w-full h-dvh overflow-hidden bg-[#0A0E27] font-body">
      <MercadoPagoCheckoutForm
        amount={PRICE_ARS}
        preferenceId={preferenceId}
        packName={PRODUCT_NAME}
        paymentType="subscription"
        metadata={{
          flow: "channel_invite_ar",
          product_type: "channel_invite",
          invitation_code: invitationCode,
        }}
        externalReference={`channel_invite_ar_${Date.now()}`}
        onSuccess={handleSuccess}
        onBack={handleBack}
        onError={handleError}
      />
    </div>
  );
}
